import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  resolveName,
  useActiveCompany,
  useActiveCompanyId,
  useBranchNameMap,
  useBranches,
  useCoa,
  useDepartmentNameMap,
  useDepartments,
  useLedger,
} from '../lib/queries';
import { exportRows, todayStamp, type ExportColumn } from '../lib/xlsx';

import type { LedgerLineWithDims } from '../lib/queries/ledger';

// 'YYYY-MM' from <input type="month"> -> 'YYYYMM' for the API.
function periodFromMonth(month: string): string {
  return month.length >= 7 ? month.slice(0, 4) + month.slice(5, 7) : '';
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Prisma's Decimal columns serialize as strings on the wire. Normalize once
// at the boundary so totals/sorting stay numeric. Mirrors journal.tsx.
function toNumber(v: string | number): number {
  return typeof v === 'string' ? Number(v) : v;
}

// Optional deep-link search params. The /journal "ดู GL" button navigates here
// with the JV's period (YYYYMM); optionally an accountCode to preselect.
export interface LedgerSearch {
  period?: string | undefined;
  accountCode?: string | undefined;
}

export const Route = createFileRoute('/ledger')({
  validateSearch: (search: Record<string, unknown>): LedgerSearch => ({
    period: typeof search.period === 'string' ? search.period : undefined,
    accountCode: typeof search.accountCode === 'string' ? search.accountCode : undefined,
  }),
  component: LedgerPage,
});

function LedgerPage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const search = Route.useSearch();
  const [month, setMonth] = useState<string>(
    search.period?.length === 6
      ? `${search.period.slice(0, 4)}-${search.period.slice(4, 6)}`
      : currentMonth(),
  );
  // Only postable leaf accounts (non-control) can be queried directly — roll-up
  // / header nodes don't have postings of their own. Default to whatever the COA
  // list returns first; the user picks from a select once data loads.
  const [accountCode, setAccountCode] = useState<string>(search.accountCode ?? '');
  // Dimension filters (Phase 42-A4) — '' = no filter. Line-level branch/dept.
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');

  const { data: coa = [], isPending: coaLoading } = useCoa(companyId);
  const leafAccounts = useMemo(() => coa.filter((c) => !c.isControl), [coa]);

  // Dimension masters + id->name maps for the filter dropdowns and row display.
  const { data: branches = [] } = useBranches(companyId);
  const { data: departments = [] } = useDepartments(companyId);
  const { map: branchNameMap } = useBranchNameMap(companyId);
  const { map: departmentNameMap } = useDepartmentNameMap(companyId);

  // Derive an effective accountCode: user's explicit pick if set, else fall back
  // to the first leaf in the COA. Avoids a setState-in-effect cascade (which the
  // lint rule react-hooks/set-state-in-effect flags) and keeps the page rendering
  // data the moment COA arrives.
  // `||` is intentional on accountCode — '' is the "no user pick" sentinel.
  // Only the fallback chain to leafAccounts[0]?.code uses ??.
  const effectiveAccountCode = accountCode !== '' ? accountCode : (leafAccounts[0]?.code ?? '');

  const period = useMemo(() => periodFromMonth(month), [month]);
  const filters = useMemo(
    () => ({
      ...(effectiveAccountCode ? { accountCode: effectiveAccountCode } : {}),
      ...(period ? { period } : {}),
      ...(branchId ? { branchId } : {}),
      ...(departmentId ? { departmentId } : {}),
    }),
    [effectiveAccountCode, period, branchId, departmentId],
  );
  const { data, isPending: ledgerLoading, error } = useLedger(companyId, filters);

  const isPending = coaLoading || (ledgerLoading && !!effectiveAccountCode);
  const rows: LedgerLineWithDims[] = data?.rows ?? [];

  // Export columns. The header keys mirror the on-screen ledger; dimension
  // columns resolve UUIDs to names via the maps already loaded for the table.
  const EXPORT_COLS: ExportColumn<LedgerLineWithDims>[] = useMemo(
    () => [
      { header: 'voucherDate', value: (l) => String(l.voucherDate).slice(0, 10), width: 12 },
      { header: 'voucherNo', value: (l) => l.voucherNo, width: 18 },
      { header: 'description', value: (l) => l.lineDescription ?? l.description, width: 40 },
      { header: 'branchName', value: (l) => resolveName(branchNameMap, l.branchId), width: 16 },
      {
        header: 'departmentName',
        value: (l) => resolveName(departmentNameMap, l.departmentId),
        width: 16,
      },
      { header: 'refKey', value: (l) => l.refKey ?? '', width: 16 },
      { header: 'debit', value: (l) => Number(l.debit), width: 14 },
      { header: 'credit', value: (l) => Number(l.credit), width: 14 },
      { header: 'runningBalance', value: (l) => Number(l.runningBalance), width: 16 },
    ],
    [branchNameMap, departmentNameMap],
  );

  async function handleExport() {
    const filename =
      'บัญชีแยกประเภท_' +
      (company?.code ?? 'COMPANY') +
      '_' +
      (effectiveAccountCode || 'all') +
      '_' +
      (period || todayStamp()) +
      '.xlsx';
    await exportRows(filename, 'บัญชีแยกประเภท', EXPORT_COLS, rows);
  }

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'บัญชีแยกประเภท']}
        title="บัญชีแยกประเภท (General Ledger)"
        actions={
          <>
            <HelpButton guide="ledger" />
            <button className="btn">
              <Icon name="print" size={14} /> พิมพ์
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={rows.length === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
          </>
        }
      />

      <div className="tbl-shell">
        <div className="filter-row">
          <select
            className="select"
            style={{ width: 360 }}
            value={effectiveAccountCode}
            onChange={(e) => setAccountCode(e.target.value)}
          >
            <option value="">— เลือกบัญชี (leaf) —</option>
            {leafAccounts.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.nameTh}
              </option>
            ))}
          </select>
          <input
            className="input w-sm"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ width: 160 }}
          />
          <select
            className="select"
            style={{ width: 180 }}
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            title="สาขา"
          >
            <option value="">สาขา: ทั้งหมด</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.nameTh}
              </option>
            ))}
          </select>
          <select
            className="select"
            style={{ width: 180 }}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            title="หน่วยงาน"
          >
            <option value="">หน่วยงาน: ทั้งหมด</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} — {d.nameTh}
              </option>
            ))}
          </select>
          {data && (
            <span className="meta">
              {data.account.type} · {data.account.nature === 'DR' ? 'เดบิต' : 'เครดิต'}-nature
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              className="btn sm"
              onClick={() => {
                setMonth(currentMonth());
                setBranchId('');
                setDepartmentId('');
              }}
            >
              รีเซ็ต
            </button>
          </div>
        </div>

        {data && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5,1fr)',
              padding: '12px 14px',
              background: '#fafafa',
              borderBottom: '1px solid var(--border)',
              gap: 14,
            }}
          >
            <div>
              <div className="faint" style={{ fontSize: 14 }}>
                ยอดยกมา
              </div>
              <div style={{ fontWeight: 600, fontSize: 16 }} className="tnum">
                {fmt(toNumber(data.openingBalance))}
              </div>
            </div>
            <div>
              <div className="faint" style={{ fontSize: 14 }}>
                เดบิต (รวม)
              </div>
              <div style={{ fontWeight: 600, fontSize: 16 }} className="tnum">
                {fmt(toNumber(data.totals.debit))}
              </div>
            </div>
            <div>
              <div className="faint" style={{ fontSize: 14 }}>
                เครดิต (รวม)
              </div>
              <div style={{ fontWeight: 600, fontSize: 16 }} className="tnum">
                {fmt(toNumber(data.totals.credit))}
              </div>
            </div>
            <div>
              <div className="faint" style={{ fontSize: 14 }}>
                ยอดคงเหลือ
              </div>
              <div
                style={{ fontWeight: 600, fontSize: 16, color: 'var(--brand)' }}
                className="tnum"
              >
                {fmt(toNumber(data.closingBalance))}
              </div>
            </div>
            <div>
              <div className="faint" style={{ fontSize: 14 }}>
                รายการ
              </div>
              <div style={{ fontWeight: 600, fontSize: 16 }} className="tnum">
                {rows.length}
              </div>
            </div>
          </div>
        )}

        <div className="tbl-wrap" style={{ maxHeight: 540 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 100 }}>วันที่</th>
                <th style={{ width: 170 }}>เลขที่ใบสำคัญ</th>
                <th>คำอธิบาย</th>
                <th style={{ width: 130 }}>สาขา</th>
                <th style={{ width: 130 }}>หน่วยงาน</th>
                <th style={{ width: 130 }}>อ้างอิง</th>
                <th className="num" style={{ width: 140 }}>
                  เดบิต
                </th>
                <th className="num" style={{ width: 140 }}>
                  เครดิต
                </th>
                <th className="num" style={{ width: 160 }}>
                  ยอดคงเหลือ
                </th>
              </tr>
            </thead>
            <tbody>
              {!effectiveAccountCode ? (
                <tr>
                  <td colSpan={9} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    เลือกบัญชีจากรายการด้านบนเพื่อแสดงรายการ
                  </td>
                </tr>
              ) : isPending ? (
                <tr>
                  <td colSpan={9} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={9}
                    className="muted"
                    style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                  >
                    โหลดล้มเหลว: {error.message}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    ไม่มีรายการในงวดนี้
                  </td>
                </tr>
              ) : (
                rows.map((l, i) => {
                  const dr = toNumber(l.debit);
                  const cr = toNumber(l.credit);
                  const bal = toNumber(l.runningBalance);
                  return (
                    <tr key={`${l.voucherNo}-${i}`}>
                      <td>{fmtDate(l.voucherDate)}</td>
                      <td className="code">
                        <span style={{ color: 'var(--brand)', fontWeight: 500 }}>
                          {l.voucherNo}
                        </span>
                      </td>
                      <td>{l.lineDescription ?? l.description}</td>
                      <td className="muted">{resolveName(branchNameMap, l.branchId)}</td>
                      <td className="muted">{resolveName(departmentNameMap, l.departmentId)}</td>
                      <td className="code muted">{l.refKey ?? ''}</td>
                      <td className="num tnum">{dr > 0 ? fmt(dr) : '—'}</td>
                      <td className="num tnum">{cr > 0 ? fmt(cr) : '—'}</td>
                      <td className="num tnum">
                        <b>{fmt(bal)}</b>
                      </td>
                    </tr>
                  );
                })
              )}
              {data && rows.length > 0 && (
                <tr className="total">
                  <td colSpan={6}>รวมเคลื่อนไหวในงวด</td>
                  <td className="num tnum">{fmt(toNumber(data.totals.debit))}</td>
                  <td className="num tnum">{fmt(toNumber(data.totals.credit))}</td>
                  <td className="num tnum">{fmt(toNumber(data.closingBalance))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
